"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RubyChatJobSchema = exports.RubyChatJobStatusSchema = exports.RubyChatRequestSchema = exports.RubyChatTurnSchema = exports.RubyChatReplySchema = exports.RubyChatOpSchema = exports.RUBY_IMAGE_SPECIAL_TARGETS = exports.RUBY_IMAGE_ACTION_ID = exports.RUBY_CHAT_MAX_IMAGES = exports.RUBY_CHAT_MAX_OPS = exports.RUBY_CHAT_MAX_HISTORY = exports.RUBY_CHAT_MAX_MESSAGE = exports.RUBY_CHAT_ACTION_ID = void 0;
exports.footerForTheme = footerForTheme;
exports.applyLookToTheme = applyLookToTheme;
exports.applyRubyChatOps = applyRubyChatOps;
exports.buildRubyChatCatalogDigest = buildRubyChatCatalogDigest;
const zod_1 = require("zod");
const manifest_1 = require("./storefront-sections/manifest");
const storefront_theme_schema_1 = require("./schemas/entities/agenticpos/storefront-theme.schema");
const storefront_theme_looks_1 = require("./storefront-theme-looks");
/**
 * Ruby Website Builder chat (theme editor). The model never writes a theme:
 * it returns a short reply, up to three look suggestions from the catalogue,
 * and a list of edit OPS. The editor applies the ops to its working copy
 * (live preview, not saved) with `applyRubyChatOps`, the same function the
 * server uses to reject anything that would not validate.
 */
exports.RUBY_CHAT_ACTION_ID = 'text.storefront_chat';
exports.RUBY_CHAT_MAX_MESSAGE = 1000;
exports.RUBY_CHAT_MAX_HISTORY = 10;
exports.RUBY_CHAT_MAX_OPS = 30;
/** Pictures Ruby may generate in one turn (each is a paid engine run). */
exports.RUBY_CHAT_MAX_IMAGES = 3;
exports.RUBY_IMAGE_ACTION_ID = 'image.multiple_objects';
/** generateImage targets that are not sections: the sign-in picture and the store logo. */
exports.RUBY_IMAGE_SPECIAL_TARGETS = { '@signin': 'image', '@brand': 'logoUrl' };
// ── Ops ──────────────────────────────────────────────────────────────────────
const Settings = zod_1.z.record(zod_1.z.unknown());
const PageId = zod_1.z.string().min(1).max(100).nullable().optional();
exports.RubyChatOpSchema = zod_1.z.discriminatedUnion('op', [
    zod_1.z.object({ op: zod_1.z.literal('applyLook'), lookId: zod_1.z.string().min(1).max(80) }),
    zod_1.z.object({ op: zod_1.z.literal('setTheme'), patch: Settings }),
    // The footer has its own colours (it does not follow the theme) + its info block.
    zod_1.z.object({ op: zod_1.z.literal('setFooter'), patch: Settings }),
    // The customer sign-in page (headline, subtext, layout, logo on/off).
    zod_1.z.object({ op: zod_1.z.literal('setSignIn'), patch: Settings }),
    // Section ops name their page; omitted = the page the merchant is viewing.
    zod_1.z.object({ op: zod_1.z.literal('updateSection'), pageId: PageId, sectionId: zod_1.z.string().min(1).max(100), settings: Settings }),
    zod_1.z.object({
        op: zod_1.z.literal('addSection'),
        pageId: PageId,
        type: zod_1.z.string().min(1).max(60),
        settings: Settings.optional(),
        afterSectionId: zod_1.z.string().max(100).nullable().optional(),
        /** Name for this new section so later ops in the same reply can target it. */
        ref: zod_1.z.string().max(40).optional(),
    }),
    // A picture for an image field, made by the Ruby Generation Engine. The editor
    // runs it and drops the finished URL into the field.
    zod_1.z.object({
        op: zod_1.z.literal('generateImage'),
        pageId: PageId,
        sectionId: zod_1.z.string().min(1).max(100),
        field: zod_1.z.string().min(1).max(60),
        prompt: zod_1.z.string().min(10).max(800),
    }),
    zod_1.z.object({ op: zod_1.z.literal('removeSection'), pageId: PageId, sectionId: zod_1.z.string().min(1).max(100) }),
    zod_1.z.object({ op: zod_1.z.literal('moveSection'), pageId: PageId, sectionId: zod_1.z.string().min(1).max(100), toIndex: zod_1.z.number().int().min(0).max(100) }),
]);
/** What the model must return (and what the job row carries back to the editor). */
exports.RubyChatReplySchema = zod_1.z.object({
    reply: zod_1.z.string().min(1).max(1200),
    suggestions: zod_1.z.array(zod_1.z.string().max(80)).max(3).default([]),
    ops: zod_1.z.array(exports.RubyChatOpSchema).max(exports.RUBY_CHAT_MAX_OPS).default([]),
});
// ── Request / job ────────────────────────────────────────────────────────────
exports.RubyChatTurnSchema = zod_1.z.object({
    role: zod_1.z.enum(['user', 'ruby']),
    text: zod_1.z.string().max(1500),
});
exports.RubyChatRequestSchema = zod_1.z.object({
    message: zod_1.z.string().trim().min(1).max(exports.RUBY_CHAT_MAX_MESSAGE),
    history: zod_1.z.array(exports.RubyChatTurnSchema).max(exports.RUBY_CHAT_MAX_HISTORY).default([]),
    /** Store identity, for copy. */
    store: zod_1.z.object({
        name: zod_1.z.string().max(200).optional(),
        categories: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string().max(100), name: zod_1.z.string().max(100) })).max(60).default([]),
    }).default({}),
    /** The editor's current state: site-wide theme settings + every page. */
    settings: Settings.default({}),
    /** Footer colours + info (columns are left out; Ruby does not rewrite links). */
    footer: Settings.default({}),
    /** Sign-in page design + whether the store has a logo yet. */
    signIn: Settings.default({}),
    hasLogo: zod_1.z.boolean().default(false),
    currentPageId: zod_1.z.string().max(100),
    /** Where the owner asked Ruby to work: the current page, or every page. */
    scope: zod_1.z.enum(['page', 'site']).default('page'),
    pages: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string().max(100),
        title: zod_1.z.string().max(200).default(''),
        slug: zod_1.z.string().max(200).default(''),
        type: zod_1.z.string().max(20).default('page'),
        sections: zod_1.z.array(zod_1.z.object({
            id: zod_1.z.string().max(100),
            type: zod_1.z.string().max(60),
            settings: Settings.default({}),
        })).max(40).default([]),
    })).min(1).max(20),
    /** Idempotency for retries of the same send. */
    clientKey: zod_1.z.string().min(8).max(100),
    /**
     * Task 25 fix round 1 (Ruby Estate): the business's storefront product line.
     * Additive + optional — missing/'rubytill' is byte-identical to the
     * pre-Ruby-Estate request shape and prompt/ops behaviour; only 'rubyestate'
     * changes the section catalogue Ruby is told about and may use.
     */
    line: zod_1.z.enum(['rubytill', 'rubyestate']).optional(),
});
exports.RubyChatJobStatusSchema = zod_1.z.enum(['queued', 'running', 'done', 'failed']);
exports.RubyChatJobSchema = zod_1.z.object({
    jobId: zod_1.z.string(),
    status: exports.RubyChatJobStatusSchema,
    result: exports.RubyChatReplySchema.optional(),
    error: zod_1.z.object({ code: zod_1.z.string(), message: zod_1.z.string() }).optional(),
    rubyTokensUsed: zod_1.z.number().optional(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
const ThemePatchSchema = storefront_theme_schema_1.ThemeSettingsSchema.removeDefault().partial();
// Ruby may restyle the footer and its info block, never rewrite its link columns.
const FooterPatchSchema = storefront_theme_schema_1.FooterConfigSchema.removeDefault()
    .pick({ bgType: true, bgColor: true, bgGradient: true, textColor: true, headingColor: true, linkColor: true })
    .extend({
    info: zod_1.z.object({
        showLogo: zod_1.z.boolean().optional(),
        tagline: zod_1.z.string().max(300).optional(),
        showPoweredBy: zod_1.z.boolean().optional(),
        showPaymentBadges: zod_1.z.boolean().optional(),
    }).optional(),
})
    .strict();
const LOOKS_BY_ID = new Map(storefront_theme_looks_1.STOREFRONT_THEME_LOOKS.map((l) => [l.id, l]));
/** Relative luminance 0..1 of a #rrggbb colour (null when unparseable). */
function luminance(hex) {
    if (typeof hex !== 'string')
        return null;
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m)
        return null;
    const [r, g, b] = [0, 2, 4].map((i) => {
        const c = parseInt(m[1].slice(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** A footer that matches a theme: the dark brand tone, readable text, accent links. */
function footerForTheme(settings) {
    const bg = settings.primaryColorDark || settings.primaryColor || '#111827';
    const dark = (luminance(bg) ?? 0) < 0.35;
    const accent = settings.accentColor;
    const accentOk = accent && luminance(accent) != null && Math.abs((luminance(accent) ?? 0) - (luminance(bg) ?? 0)) > 0.3;
    return {
        bgType: 'solid', bgColor: bg,
        textColor: dark ? '#e7e5e4' : '#292524',
        headingColor: dark ? '#ffffff' : '#0c0a09',
        linkColor: accentOk ? accent : (dark ? '#f5f5f4' : '#1c1917'),
    };
}
/** Per-section colour overrides that would fight a new look. */
const SECTION_COLOUR_KEYS = ['bgType', 'bgColor', 'bgColor2', 'bgGradient', 'gradientAngle', 'textColor', 'buttonColor', 'buttonTextColor'];
/**
 * Apply a look as a COMPLETE design: theme settings, a matching footer, and
 * every section's colour overrides cleared so all of them follow the look (an
 * old black text colour on a new dark look is how pages became unreadable).
 * Pictures, copy and layout are kept.
 */
function applyLookToTheme(config, lookId, 
/** Image CDN origin (e.g. https://image.rubytill.com): fills an empty home hero with the look's photograph. */
opts = {}) {
    const look = LOOKS_BY_ID.get(lookId);
    if (!look)
        return null;
    const photo = opts.imageOrigin ? `${opts.imageOrigin.replace(/\/$/, '')}/${(0, storefront_theme_looks_1.lookImagePath)(look.id)}` : null;
    const homeId = (config.pages ?? []).find((p) => p.type === 'home')?.id ?? config.pages?.[0]?.id;
    let heroFilled = false;
    const settings = { ...(config.settings ?? {}), ...look.settings };
    const clean = (sec, pageId) => {
        const next = { ...(sec.settings ?? {}) };
        for (const k of SECTION_COLOUR_KEYS)
            delete next[k];
        // A look builds a complete page: the first home hero without a picture gets the look's photograph.
        if (photo && !heroFilled && pageId === homeId && sec.type === 'hero') {
            heroFilled = true;
            if (!next.bgImage && !next.imageUrl) {
                next.bgImage = photo;
                next.overlayOpacity = typeof next.overlayOpacity === 'number' && next.overlayOpacity >= 35 ? next.overlayOpacity : 45;
            }
        }
        return { ...sec, settings: next };
    };
    return {
        ...config,
        settings: settings,
        pages: (config.pages ?? []).map((p) => ({ ...p, sections: (p.sections ?? []).map((sec) => clean(sec, p.id)) })),
        footerConfig: { columns: [], info: {}, ...(config.footerConfig ?? {}), ...footerForTheme(settings) },
    };
}
function stripUndefined(o) {
    return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));
}
/**
 * Apply Ruby's ops to a theme. Theme ops are site-wide; section ops go to
 * their `pageId` (default: `currentPageId`). Pure: returns a new config.
 * Invalid ops are skipped (and reported), never thrown, so one bad op cannot
 * lose the rest of a reply. `newId` makes section ids deterministic in tests.
 */
function applyRubyChatOps(config, currentPageId, rawOps, newId = () => `sec_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`, 
/**
 * Image CDN origin, so applyLook can fill an empty hero with the look's photograph.
 * `line` (Task 25, Ruby Estate) restricts `addSection` to the same section set the
 * editor offers for that business — missing/'rubytill' ⇒ unchanged (category
 * 'rentals' stays off-limits); 'rubyestate' excludes category 'products' instead.
 */
opts = {}) {
    let settings = { ...(config.settings ?? {}) };
    let footerConfig = config.footerConfig ? { ...config.footerConfig } : undefined;
    let account = config.account ? { ...config.account } : undefined;
    const pages = (config.pages ?? []).map((p) => ({ ...p, sections: [...(p.sections ?? [])] }));
    const applied = [];
    const rejected = [];
    const allowedTypes = new Set((0, manifest_1.sectionsForLine)(opts.line).map((m) => m.type));
    /** addSection refs → the real ids they were given. */
    const refs = new Map();
    let images = 0;
    for (const raw of rawOps.slice(0, exports.RUBY_CHAT_MAX_OPS)) {
        const parsed = exports.RubyChatOpSchema.safeParse(raw);
        if (!parsed.success) {
            rejected.push({ op: raw, reason: 'invalid_op' });
            continue;
        }
        const op = parsed.data;
        if (op.op === 'applyLook') {
            const look = LOOKS_BY_ID.get(op.lookId);
            if (!look) {
                rejected.push({ op, reason: 'unknown_look' });
                continue;
            }
            // A look is a complete design (theme + footer + sections follow it).
            const designed = applyLookToTheme({ ...config, settings: settings, pages, footerConfig }, op.lookId, opts);
            settings = designed.settings;
            footerConfig = designed.footerConfig;
            designed.pages.forEach((dp, i) => { pages[i].sections = dp.sections; });
            applied.push(op);
            continue;
        }
        if (op.op === 'setTheme') {
            const p = ThemePatchSchema.safeParse(op.patch);
            if (!p.success) {
                rejected.push({ op, reason: 'invalid_theme_patch' });
                continue;
            }
            // Ruby's own colours pass the same readability guard as the looks.
            settings = (0, storefront_theme_looks_1.ensureReadableSettings)({ ...settings, ...stripUndefined(p.data) });
            applied.push(op);
            continue;
        }
        if (op.op === 'setSignIn') {
            const p = storefront_theme_schema_1.SignInConfigSchema.omit({ image: true }).strict().safeParse(op.patch);
            if (!p.success) {
                rejected.push({ op, reason: 'invalid_signin_patch' });
                continue;
            }
            const prev = (account?.signIn ?? {});
            account = { ...(account ?? {}), signIn: { ...prev, ...stripUndefined(p.data) } };
            applied.push(op);
            continue;
        }
        if (op.op === 'generateImage' && op.sectionId in exports.RUBY_IMAGE_SPECIAL_TARGETS) {
            if (exports.RUBY_IMAGE_SPECIAL_TARGETS[op.sectionId] !== op.field) {
                rejected.push({ op, reason: 'not_an_image_field' });
                continue;
            }
            if (++images > exports.RUBY_CHAT_MAX_IMAGES) {
                rejected.push({ op, reason: 'too_many_images' });
                continue;
            }
            applied.push(op);
            continue;
        }
        if (op.op === 'setFooter') {
            const p = FooterPatchSchema.safeParse(op.patch);
            if (!p.success) {
                rejected.push({ op, reason: 'invalid_footer_patch' });
                continue;
            }
            const { info, ...style } = p.data;
            // A plain colour must win over a saved gradient.
            if (style.bgColor && !style.bgType && !style.bgGradient)
                style.bgType = 'solid';
            const base = (footerConfig ?? { columns: [], info: {} });
            footerConfig = {
                ...base,
                ...stripUndefined(style),
                ...(info ? { info: { ...(base.info ?? {}), ...stripUndefined(info) } } : {}),
            };
            applied.push(op);
            continue;
        }
        const page = pages.find((p) => p.id === (op.pageId || currentPageId));
        if (!page) {
            rejected.push({ op, reason: 'unknown_page' });
            continue;
        }
        const realId = (id) => refs.get(id) ?? id;
        const idx = (id) => page.sections.findIndex((s) => s.id === realId(id));
        if (op.op === 'generateImage') {
            const i = idx(op.sectionId);
            if (i < 0) {
                rejected.push({ op, reason: 'unknown_section' });
                continue;
            }
            const field = (0, manifest_1.getManifest)(page.sections[i].type)?.fields?.find((f) => f.name === op.field);
            if (!field || field.control !== 'image') {
                rejected.push({ op, reason: 'not_an_image_field' });
                continue;
            }
            if (++images > exports.RUBY_CHAT_MAX_IMAGES) {
                rejected.push({ op, reason: 'too_many_images' });
                continue;
            }
            // Nothing changes yet — the editor fills the field when the picture is ready.
            applied.push({ ...op, pageId: page.id, sectionId: page.sections[i].id });
            continue;
        }
        if (op.op === 'updateSection') {
            const i = idx(op.sectionId);
            if (i < 0) {
                rejected.push({ op, reason: 'unknown_section' });
                continue;
            }
            const next = { ...page.sections[i], settings: { ...page.sections[i].settings, ...op.settings } };
            const v = (0, manifest_1.validateSection)(next);
            if (!v.ok) {
                rejected.push({ op, reason: v.error });
                continue;
            }
            page.sections[i] = { ...next, settings: v.settings, blocks: v.blocks };
            applied.push(op);
        }
        else if (op.op === 'addSection') {
            const m = (0, manifest_1.getManifest)(op.type);
            if (!m || m.hidden || !allowedTypes.has(op.type)) {
                rejected.push({ op, reason: 'unknown_section_type' });
                continue;
            }
            if (m.singleton && page.sections.some((s) => s.type === op.type)) {
                rejected.push({ op, reason: 'singleton_exists' });
                continue;
            }
            const next = {
                id: newId(), type: op.type, visible: true,
                settings: { ...m.defaults(), ...(op.settings ?? {}) }, blocks: [],
            };
            const v = (0, manifest_1.validateSection)(next);
            if (!v.ok) {
                rejected.push({ op, reason: v.error });
                continue;
            }
            const section = { ...next, settings: v.settings, blocks: v.blocks };
            if (op.ref)
                refs.set(op.ref, section.id);
            const after = op.afterSectionId ? idx(op.afterSectionId) : -1;
            if (after >= 0)
                page.sections.splice(after + 1, 0, section);
            else
                page.sections.push(section);
            applied.push(op);
        }
        else if (op.op === 'removeSection') {
            const i = idx(op.sectionId);
            if (i < 0) {
                rejected.push({ op, reason: 'unknown_section' });
                continue;
            }
            page.sections.splice(i, 1);
            applied.push(op);
        }
        else if (op.op === 'moveSection') {
            const i = idx(op.sectionId);
            if (i < 0) {
                rejected.push({ op, reason: 'unknown_section' });
                continue;
            }
            const [s] = page.sections.splice(i, 1);
            page.sections.splice(Math.min(op.toIndex, page.sections.length), 0, s);
            applied.push(op);
        }
    }
    // Brand colours changed but the footer was not addressed → keep it matching.
    const recoloured = applied.some((o) => o.op === 'setTheme' && ['primaryColor', 'primaryColorDark', 'backgroundColor'].some((k) => k in o.patch));
    if (recoloured && !applied.some((o) => o.op === 'setFooter' || o.op === 'applyLook')) {
        footerConfig = { columns: [], info: {}, ...(footerConfig ?? {}), ...footerForTheme(settings) };
    }
    return {
        config: {
            ...config, settings: settings, pages,
            ...(footerConfig ? { footerConfig } : {}), ...(account ? { account } : {}),
        },
        applied, rejected,
    };
}
// ── Prompt digest (static, cacheable) ───────────────────────────────────────
/**
 * The fixed part of Ruby's instructions: every section type with its editable
 * fields, the theme setting keys, and the look catalogue. Identical across
 * tenants and turns for one `line`, so it is prompt-cached by the vendor per line.
 * `line` (Task 25, Ruby Estate) is the SAME `sectionsForLine` helper the editor's
 * "Add section" sheet uses, so the two can never disagree — missing/'rubytill'
 * is byte-identical to the pre-Ruby-Estate digest (category 'rentals' excluded).
 */
function buildRubyChatCatalogDigest(line) {
    const sections = (0, manifest_1.sectionsForLine)(line)
        .filter((m) => !m.hidden)
        .map((m) => {
        const fields = (m.fields ?? [])
            .filter((f) => !['product-ids', 'brand-ids', 'playlist-id'].includes(f.control))
            .map((f) => {
            if (f.control === 'image')
                return `${f.name}:IMAGE(use generateImage)`;
            const opts = f.options?.length ? `=${f.options.map((o) => o.value).join('|')}` : '';
            const range = f.control === 'number' && (f.min != null || f.max != null) ? `(${f.min ?? ''}..${f.max ?? ''})` : '';
            return `${f.name}:${f.control}${opts}${range}`;
        });
        return `- ${m.type}${m.singleton ? ' (once per page)' : ''}: ${m.label}. Fields: ${fields.join(', ') || '(none editable)'}`;
    })
        .join('\n');
    const cats = new Map(storefront_theme_looks_1.STOREFRONT_LOOK_CATEGORIES.map((c) => [c.id, c.label]));
    const looks = storefront_theme_looks_1.STOREFRONT_THEME_LOOKS
        .map((l) => `- ${l.id} | ${l.label} | ${cats.get(l.category) ?? l.category} | ${l.description}`)
        .join('\n');
    const themeKeys = [
        'primaryColor', 'primaryColorDark', 'accentColor', 'backgroundColor', 'inkColor (hex colours)',
        'displayFont, bodyFont = Inter|Fraunces|Poppins|Playfair Display|Montserrat|Roboto',
        'headingWeight = normal|medium|semibold|bold', 'fontScale = compact|normal|spacious',
        'buttonRadius = sharp|rounded|pill', 'buttonStyle = solid|outline', 'cornerRadius = sharp|soft|round',
        'density = compact|cozy|spacious', 'containerWidth = standard|wide',
        'backgroundType = solid|gradient', 'backgroundGradient = {angle, stops:[{color, position 0-100}]}',
    ].join('; ');
    const footerKeys = 'bgType = solid|gradient; bgColor; bgGradient = {angle, stops:[{color, position}]}; textColor; headingColor; linkColor (hex colours); info = {showLogo, tagline, showPoweredBy, showPaymentBadges}';
    const signInKeys = 'headline (max 120); subtext (max 300); layout = card|split|minimal (split = picture beside the form); showLogo (boolean). Picture: generateImage sectionId "@signin" field "image". Store logo: generateImage sectionId "@brand" field "logoUrl".';
    return `SECTION TYPES (use only these; set only listed fields):\n${sections}\n\nTHEME SETTINGS (setTheme.patch keys): ${themeKeys}\n\nSIGN-IN PAGE (setSignIn.patch keys; the customer login page, styled by the theme): ${signInKeys}\n\nFOOTER (setFooter.patch keys; the footer has its OWN colours and does not follow the theme): ${footerKeys}\n\nLOOK CATALOGUE (id | name | category | description):\n${looks}`;
}
//# sourceMappingURL=storefront-ruby-chat.js.map